/**
 * MYS-140 — "Lien de paiement : pouvoir l'envoyer même si le joueur n'est pas
 * dans la base de données." Helpers for the guest-player path of the
 * "Réserver pour un joueur" modal: composing the phone the API keys guests by,
 * and the share links the operator uses to send the payment link to a player
 * who has no app.
 */

export type GuestPhoneCountry = {
    callingCode: string;
    label: string;
};

// France first — every live tenant today is French. The rest are the
// neighbours and the Maghreb, where most venue clients' visitors come from.
// The operator can still paste a full "+…" number into the field, which wins
// over this picker (see `composeGuestPhoneE164`).
export const GUEST_PHONE_COUNTRIES: GuestPhoneCountry[] = [
    { callingCode: '33', label: 'France (+33)' },
    { callingCode: '32', label: 'Belgique (+32)' },
    { callingCode: '41', label: 'Suisse (+41)' },
    { callingCode: '352', label: 'Luxembourg (+352)' },
    { callingCode: '377', label: 'Monaco (+377)' },
    { callingCode: '34', label: 'Espagne (+34)' },
    { callingCode: '39', label: 'Italie (+39)' },
    { callingCode: '351', label: 'Portugal (+351)' },
    { callingCode: '49', label: 'Allemagne (+49)' },
    { callingCode: '44', label: 'Royaume-Uni (+44)' },
    { callingCode: '212', label: 'Maroc (+212)' },
    { callingCode: '213', label: 'Algérie (+213)' },
    { callingCode: '216', label: 'Tunisie (+216)' },
    { callingCode: '1', label: 'États-Unis / Canada (+1)' },
];

export const DEFAULT_GUEST_PHONE_CALLING_CODE = '33';

// The API's own bound (`find-or-create-guest-player.ts`): E.164 with the "+".
const E164_PHONE_PATTERN = /^\+[1-9]\d{6,14}$/;

// Countries whose numbers keep their leading 0 in international form (Italian
// landlines: 06 … → +39 06 …). Everywhere else the 0 is a trunk prefix that
// must be dropped (06 12 … in France → +33 6 12 …).
const KEEPS_LEADING_ZERO = new Set(['39']);

// Longest national significant number (no trunk 0) for the picker's countries
// whose numbering plans have a fixed length. Italy, Germany and Luxembourg use
// variable lengths and are left out: for them no length can say "this is
// already international", so what was typed is taken as national.
const NATIONAL_NUMBER_MAX_DIGITS: Record<string, number> = {
    '33': 9,
    '32': 9,
    '41': 9,
    '377': 9,
    '34': 9,
    '351': 9,
    '44': 10,
    '212': 9,
    '213': 9,
    '216': 8,
    '1': 10,
};

// "+33 (0)6 12 34 56 78": the "(0)" is the national trunk prefix, which is not
// dialled from abroad. Dropped BEFORE the parentheses are stripped — after,
// "+330612345678" passes E.164 but is a different, non-existent number. Same
// rule as the API's `normalizeGuestPhoneNumber` (MYS-140 run 6).
const TRUNK_PREFIX_NOTATION = /^(\s*(?:\+|00)\s*\d{1,3})\s*\(\s*0\s*\)/;

/**
 * Turns what an operator typed into E.164, or null when it cannot be one.
 * A number typed in international form ("+44 …", "0044 …") is taken as is and
 * the country picker is ignored; otherwise the picker's calling code is
 * prepended and the national trunk 0 dropped.
 */
export function composeGuestPhoneE164(
    callingCode: string,
    typed: string,
): string | null {
    const compact = (typed ?? '')
        .replace(TRUNK_PREFIX_NOTATION, '$1')
        .replace(/[\s.\-()/]/g, '');
    if (!compact) return null;

    let e164: string;
    if (compact.startsWith('+')) {
        e164 = compact;
    } else if (compact.startsWith('00')) {
        e164 = `+${compact.slice(2)}`;
    } else {
        let national = compact;
        // "33 6 12 34 56 78" with France picked: the operator typed the country
        // code without its "+". Prepending the picker's code again gives
        // +3333612345678 — valid E.164, wrong number. Only for countries whose
        // national numbers have a fixed length can the two be told apart, and
        // there a number that starts with the code and is too long to be
        // national is international.
        const maxNational = NATIONAL_NUMBER_MAX_DIGITS[callingCode];
        if (
            maxNational &&
            national.startsWith(callingCode) &&
            national.length > maxNational
        ) {
            national = national.slice(callingCode.length);
        }
        if (national.startsWith('0') && !KEEPS_LEADING_ZERO.has(callingCode)) {
            national = national.slice(1);
        }
        if (maxNational && national.length > maxNational) return null;
        e164 = `+${callingCode}${national}`;
    }
    return E164_PHONE_PATTERN.test(e164) ? e164 : null;
}

// Same shape the API accepts (`admin-booking-recipient.ts`) — the server is
// the judge; this only stops the operator submitting something it will refuse.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;
export const GUEST_EMAIL_MAX_LENGTH = 254;

export function isPlausibleGuestEmail(value: string): boolean {
    const trimmed = (value ?? '').trim();
    return (
        trimmed.length > 0 &&
        trimmed.length <= GUEST_EMAIL_MAX_LENGTH &&
        EMAIL_PATTERN.test(trimmed) &&
        !/@guest\.internal$/i.test(trimmed)
    );
}

/**
 * Splits what the operator typed into the player search ("Jean Dupont") into a
 * first/last name to prefill the guest form with. Only a plain name is used —
 * an email or a phone typed into the search box prefills nothing.
 */
export function splitSearchIntoName(query: string): {
    firstName: string;
    lastName: string;
} {
    const trimmed = (query ?? '').trim();
    if (!trimmed || /[@\d]/.test(trimmed)) return { firstName: '', lastName: '' };
    const [first, ...rest] = trimmed.split(/\s+/);
    return { firstName: first, lastName: rest.join(' ') };
}

/** The message pre-filled into WhatsApp / SMS for a payment link. */
export function buildPaymentLinkShareMessage({
    firstName,
    checkoutUrl,
    expiresAt,
}: {
    firstName?: string | null;
    checkoutUrl: string;
    expiresAt?: string | null;
}): string {
    const greeting = firstName?.trim() ? `Bonjour ${firstName.trim()}, ` : 'Bonjour, ';
    let deadline = '';
    if (expiresAt) {
        const date = new Date(expiresAt);
        if (!Number.isNaN(date.getTime())) {
            deadline = ` Le lien est valable jusqu'au ${date.toLocaleString('fr-FR', {
                day: 'numeric',
                month: 'long',
                hour: '2-digit',
                minute: '2-digit',
            })}.`;
        }
    }
    return `${greeting}voici le lien pour confirmer et payer ta réservation : ${checkoutUrl}${deadline}`;
}

/**
 * The digits of a STORED phone when it is unambiguously international, else
 * null. Guests are stored in E.164 ("+33…") or, by the WhatsApp assistant, in
 * E.164 without the "+" ("33…"); a registered player's phone is saved as the
 * app sent it, which can be national ("06…"). A national number has no
 * country, so a link built from it ("wa.me/0612…", "sms:+0612…") reaches
 * someone else or nobody — no link is better than a wrong one.
 */
function internationalDigits(phoneNumber: string | null | undefined): string | null {
    const compact = (phoneNumber ?? '').replace(/[\s.\-()/]/g, '');
    const withPlus = compact.startsWith('00')
        ? `+${compact.slice(2)}`
        : compact.startsWith('+')
          ? compact
          : `+${compact}`;
    return E164_PHONE_PATTERN.test(withPlus) ? withPlus.slice(1) : null;
}

/** wa.me wants the number as digits only, no "+". Null when there is none. */
export function buildWhatsAppShareUrl(
    phoneNumber: string | null | undefined,
    message: string,
): string | null {
    const digits = internationalDigits(phoneNumber);
    if (!digits) return null;
    return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
}

/**
 * `sms:` with `?&body=` — the one spelling both iOS/macOS Messages and Android
 * read the body from. The number keeps its "+" (WhatsApp-stored guests have
 * none; one is added so the SMS app does not read it as a national number).
 */
export function buildSmsShareUrl(
    phoneNumber: string | null | undefined,
    message: string,
): string | null {
    const digits = internationalDigits(phoneNumber);
    if (!digits) return null;
    return `sms:+${digits}?&body=${encodeURIComponent(message)}`;
}
